import { Token } from "./tokenize";
import { ProgramNode, ExpressionNode, TextNode, IdentifierNode } from "./ast";

export const parse = (tokens: Token[]): ProgramNode => {
  const program: ProgramNode = {
    type: "Program",
    body: [],
  };

  const updateStackEnds = (end: number) => {
    stack.forEach((exp) => {
      exp.end = end;
    });
  };

  const concatText = (text: TextNode) => {
    const prev = program.body[program.body.length - 1];

    if (prev && prev.type === "Text") {
      prev.value += text.value;
      prev.end = text.end;
    } else {
      program.body.push(text);
    }
  };

  let stack: ExpressionNode[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const prevToken = tokens[i - 1];

    switch (token.type) {
      case "OpenBrace": {
        /**
         * If the stack is not empty, then we are inside an expression.
         */
        if (stack.length > 0) {
          throw new Error(`Unexpected "\${" at ${token.start}`);
        }

        const node: ExpressionNode = {
          type: "Expression",
          identifier: undefined,
          property: undefined,
          start: token.start,
          end: token.end,
        };

        stack.push(node);

        break;
      }

      case "CloseBrace": {
        /**
         * If the stack is empty, then we are not inside an expression.
         */
        if (stack.length === 0) {
          throw new Error(`Unexpected "}" at ${token.start}`);
        }

        const [first] = stack;

        updateStackEnds(token.end);

        program.body.push(first);
        stack = [];

        break;
      }

      case "Dot": {
        if (prevToken?.type === "Dot") {
          throw new Error(`Unexpected ".." at ${token.start}`);
        }

        const parent = stack[stack.length - 1];

        /**
         * If a `.` exists with out a parent expression, then we are not inside an expression.
         */
        if (!parent) {
          const node: TextNode = {
            type: "Text",
            value: ".",
            start: token.start,
            end: token.end,
          };

          concatText(node);
        } else if (parent.identifier) {
          const node: ExpressionNode = {
            type: "Expression",
            identifier: undefined,
            property: undefined,
            start: token.start,
            end: token.end,
          };

          parent.property = node;
          stack.push(node);
        } else {
          throw new Error(`Unexpected "\${." at ${token.start}`);
        }

        break;
      }

      case "Text": {
        /**
         * If the stack is not empty, then we are inside an expression so this is an identifier
         */
        if (stack.length > 0) {
          /**
           * If the identifier contains whitespace after trimming, then it is invalid.
           * For example with, "${foo.bar    baz.qux}", the identifier would be "bar    baz" which is invalid.
           */
          if (token.value.trim().split(" ").length > 1) {
            throw new Error(`Unexpected whitespace in "${token.value}" at ${token.start}`);
          }

          const node: IdentifierNode = {
            type: "Identifier",
            value: token.value,
            start: token.start,
            end: token.end,
          };

          const parent = stack[stack.length - 1];
          parent.identifier = node;
        } else {
          const node: TextNode = {
            type: "Text",
            value: token.value,
            start: token.start,
            end: token.end,
          };

          concatText(node);
        }

        break;
      }
    }
  }

  if (stack.length > 0) {
    const [first] = stack;
    program.body.push(first);
  }

  return program;
};
